import type { ProcessResult } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { analyses } from './folder-analyses';

/**
 * A setup proposal as the reviewer left it, kept on the job's engine host so a reload finds it
 * (parity S-13). CapturePoint's desktop writes the amended sample into the setup's sidecar file;
 * Paige held it in the page only (folder-analysis.ts), and a reload showed the reader's first
 * proposal again. The host answers it only while it is still the proposal on the table: the job
 * has no document types yet and its sample has not been read again (the host's SetupEvidence).
 */
interface KeptProposal {
  result: ProcessResult;
  evidenceRevision: number;
}

/** Keeps the amended proposal on the host. A refusal is the caller's to show: the page still has it. */
export const keepProposal = (call: EngineCall, jobId: string, result: ProcessResult) =>
  call<unknown>('save-setup-evidence', { jobId, batch: result });

/** Forgets the host's copy (the sample discarded); a host that is away forgets it on the next read. */
export const dropProposal = (call: EngineCall, jobId: string): Promise<void> =>
  call<unknown>('discard-setup-evidence', { jobId }).then(
    () => undefined,
    () => undefined,
  );

/**
 * Puts the host's kept proposal back as this page's analysis, ready, when the page holds none.
 * A host that keeps nothing, or cannot be asked, leaves the page as it was: the job's stored
 * proposal is still adopted by the App, as before.
 */
export async function restoreProposal(call: EngineCall, jobId: string): Promise<boolean> {
  let kept: KeptProposal | null;
  try {
    kept = await call<KeptProposal | null>('setup-evidence', { jobId });
  } catch {
    return false;
  }
  if (!kept?.result?.bootstrapProposal?.documentTypes.length || analyses.has(jobId)) return false;
  analyses.set(jobId, {
    state: 'ready',
    startedAt: kept.result.startedAt,
    totalFileCount: kept.result.documents.length,
    analyzedFileCount: kept.result.documents.length,
    result: kept.result,
    failure: null,
    evidenceRevision: kept.evidenceRevision,
    progress: null,
  });
  return true;
}
